import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ProductKey } from "../../../shared/products";

/**
 * Dashboard settings interface
 * Maps dashboard widget keys to their visibility state
 */
export interface DashboardSettings {
  [key: string]: boolean;
}

/**
 * Per-portal navigation state persisted per user.
 * Queues are portal-level (shared across work types), so we only track
 * whether the Queues section is collapsed and which queues are pinned.
 */
export interface PortalNavState {
  queuesCollapsed: boolean;
  pinnedQueueIds: string[];
}

/**
 * Which product the app opens into on entry. Absent = service-desk.
 * Sourced from shared/products.ts so this and the server-side sanitiser
 * (server/routes/userPreferences.sanitize.ts) cannot drift — the sanitiser
 * silently drops values it does not recognise.
 */
export type DefaultLanding = ProductKey;

export interface UiPreferences {
  portalNav?: Record<string, PortalNavState>;
  /** Preferred common-nav app ordering (app slugs), global per user. */
  navOrder?: string[];
  /** Which product the app opens into. Absent = service-desk. */
  defaultLanding?: DefaultLanding;
}

/**
 * User preferences state interface
 * Manages user-specific preferences and settings
 */
export interface UserPreferencesState {
  /**
   * Instance timezone preference
   * Format: IANA timezone string (e.g., 'America/New_York')
   */
  instanceTimezone: string | null;

  /**
   * Dashboard widget visibility settings
   * Maps widget identifiers to their visibility state
   */
  dashboardSettings: DashboardSettings;

  /**
   * Dashboard widget order
   * Array of widget keys determining display order
   */
  dashboardWidgetOrder: string[];

  /**
   * Last visited route for session restoration
   */
  lastVisitedRoute: string | null;

  /**
   * Arbitrary per-user UI state persisted through the preferences API.
   * Currently holds portalNav expansion and pinned queues per portal.
   */
  uiPreferences: UiPreferences;
}

const initialState: UserPreferencesState = {
  instanceTimezone: null,
  dashboardSettings: {},
  dashboardWidgetOrder: [],
  lastVisitedRoute: null,
  uiPreferences: {},
};

/**
 * Ensure state.uiPreferences exists. Needed because redux-persist may rehydrate
 * state blobs from before uiPreferences was introduced, bypassing initialState.
 */
function ensureUiPreferences(state: UserPreferencesState): void {
  if (!state.uiPreferences) state.uiPreferences = {};
}

function getPortalNav(state: UserPreferencesState, portalKey: string): PortalNavState {
  ensureUiPreferences(state);
  const existing = state.uiPreferences.portalNav?.[portalKey];
  return existing ?? { queuesCollapsed: false, pinnedQueueIds: [] };
}

function setPortalNav(state: UserPreferencesState, portalKey: string, next: PortalNavState): void {
  ensureUiPreferences(state);
  if (!state.uiPreferences.portalNav) state.uiPreferences.portalNav = {};
  state.uiPreferences.portalNav[portalKey] = next;
}

/**
 * User Preferences slice for managing user-specific settings
 *
 * This slice handles user preferences that should persist across sessions,
 * including timezone settings, dashboard widget visibility, and navigation state.
 *
 * @example
 * ```typescript
 * // Set timezone
 * dispatch(setInstanceTimezone('America/New_York'));
 *
 * // Update dashboard settings
 * dispatch(setDashboardSettings({ widget1: true, widget2: false }));
 *
 * // Toggle individual widget
 * dispatch(toggleDashboardWidget('widget1'));
 *
 * // Set last visited route
 * dispatch(setLastVisitedRoute('/dashboard'));
 *
 * // Reset all preferences
 * dispatch(resetUserPreferences());
 * ```
 */
const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    /**
     * Set the instance timezone preference
     * @param state - Current user preferences state
     * @param action - Payload containing the IANA timezone string
     */
    setInstanceTimezone: (state, action: PayloadAction<string>) => {
      state.instanceTimezone = action.payload;
    },

    /**
     * Set all dashboard widget visibility settings
     * @param state - Current user preferences state
     * @param action - Payload containing dashboard settings object
     */
    setDashboardSettings: (state, action: PayloadAction<DashboardSettings>) => {
      state.dashboardSettings = action.payload;
    },

    /**
     * Toggle visibility of a specific dashboard widget
     * @param state - Current user preferences state
     * @param action - Payload containing the widget key to toggle
     */
    toggleDashboardWidget: (state, action: PayloadAction<string>) => {
      const widgetKey = action.payload;
      state.dashboardSettings[widgetKey] = !state.dashboardSettings[widgetKey];
    },

    /**
     * Set dashboard widget order
     * @param state - Current user preferences state
     * @param action - Payload containing array of widget keys in desired order
     */
    setDashboardWidgetOrder: (state, action: PayloadAction<string[]>) => {
      state.dashboardWidgetOrder = action.payload;
    },

    /**
     * Set the last visited route for session restoration
     * @param state - Current user preferences state
     * @param action - Payload containing the route path
     */
    setLastVisitedRoute: (state, action: PayloadAction<string>) => {
      state.lastVisitedRoute = action.payload;
    },

    /**
     * Replace the full uiPreferences blob. Used to hydrate from the server on app load.
     */
    hydrateUiPreferences: (state, action: PayloadAction<UiPreferences>) => {
      state.uiPreferences = action.payload ?? {};
    },

    /**
     * Toggle whether the Queues section is collapsed in a portal's sidebar.
     */
    togglePortalQueuesCollapsed: (state, action: PayloadAction<{ portalKey: string }>) => {
      const { portalKey } = action.payload;
      const nav = getPortalNav(state, portalKey);
      setPortalNav(state, portalKey, { ...nav, queuesCollapsed: !nav.queuesCollapsed });
    },

    /**
     * Toggle a queue's pinned state for a portal.
     */
    togglePortalQueuePin: (
      state,
      action: PayloadAction<{ portalKey: string; queueId: string }>,
    ) => {
      const { portalKey, queueId } = action.payload;
      const nav = getPortalNav(state, portalKey);
      const idx = nav.pinnedQueueIds.indexOf(queueId);
      const pinnedQueueIds =
        idx >= 0
          ? nav.pinnedQueueIds.filter((id) => id !== queueId)
          : [...nav.pinnedQueueIds, queueId];
      setPortalNav(state, portalKey, { ...nav, pinnedQueueIds });
    },

    /**
     * Reset all user preferences to initial state
     * @param state - Current user preferences state
     */
    resetUserPreferences: (state) => {
      state.instanceTimezone = null;
      state.dashboardSettings = {};
      state.dashboardWidgetOrder = [];
      state.lastVisitedRoute = null;
      state.uiPreferences = {};
    },
  },
});

export const {
  setInstanceTimezone,
  setDashboardSettings,
  setDashboardWidgetOrder,
  toggleDashboardWidget,
  setLastVisitedRoute,
  hydrateUiPreferences,
  togglePortalQueuesCollapsed,
  togglePortalQueuePin,
  resetUserPreferences,
} = userPreferencesSlice.actions;

export default userPreferencesSlice.reducer;
